import { Collapsible } from '@base-ui/react/collapsible';
import styled, { css } from 'styled-components';
import { LoadingBars } from 'modules/LoadingBars';
import { Typography } from 'modules/Typography';
import { pxToRem } from 'modules/spacing';
import { theme } from 'modules/tetrisTheme';
const MARK_SIZE = pxToRem(16);
const STAGGERED_STEPS = 6;
export const LABEL_SWAP_DURATION = 200;
export const Container = styled(Collapsible.Root) `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 42ch;
`;
export const TriggerIcon = styled.span `
  display: inline-flex;
  flex-shrink: 0;
`;
export const Chevron = styled(TriggerIcon) `
  transition: transform 200ms ease;
`;
export const Bars = styled(LoadingBars) `
  color: ${theme.swatches.warm[400]};

  --loading-bars-accent: ${theme.swatches.warm[700]};
`;
export const TriggerLabel = styled.span `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${({ $swapping }) => ($swapping ? 0 : 1)};
  transition: opacity ${LABEL_SWAP_DURATION}ms ease;
`;
export const TriggerMeta = styled.span `
  flex-shrink: 0;
  font-weight: ${theme.fontWeight.regular};
`;
const triggerBase = css `
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.md};
  max-width: 100%;
  height: ${pxToRem(28)};
  font-size: ${theme.fontSize.small};
  font-weight: ${theme.fontWeight.medium};
  line-height: 1;
  color: ${theme.swatches.warm[500]};
`;
export const Status = styled.output `
  ${triggerBase};
`;
export const Trigger = styled(Collapsible.Trigger) `
  ${triggerBase};
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: ${theme.borderRadius.md};
  font-family: inherit;
  cursor: pointer;
  transition: color 160ms ease;

  & [data-icon='falcon'] {
    color: ${theme.swatches.warm[500]};
    transition: color 160ms ease;
  }

  &:hover,
  &:hover [data-icon='falcon'] {
    color: ${theme.swatches.warm[700]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.focus.ring};
  }

  &[data-panel-open] ${Chevron} {
    transform: rotate(90deg);
  }
`;
export const Panel = styled(Collapsible.Panel) `
  width: 100%;
  height: var(--collapsible-panel-height);
  overflow: hidden;
  transition: height 240ms cubic-bezier(0.4, 0, 0.2, 1);

  &[data-starting-style],
  &[data-ending-style] {
    height: 0;
  }
`;
/** `--rail-gap` spaces the steps and sizes the rail segments between their marks. */
export const Steps = styled.ol `
  --rail-gap: ${({ $compact }) => ($compact ? theme.spacing.xs : theme.spacing.sm)};

  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: var(--rail-gap) 0 ${theme.spacing.xs};

  ${({ $boxed }) => $boxed &&
    css `
      margin-top: ${theme.spacing.sm};
      padding: ${theme.spacing.lg};
      background: ${theme.semantic.surface.primary};
      border: ${pxToRem(1)} solid ${theme.semantic.border.default};
      border-radius: ${theme.borderRadius.md};
    `}
`;
const staggerSteps = Array.from({ length: STAGGERED_STEPS }, (_, index) => css `
    &:nth-child(${index + 1}) {
      transition-delay: ${(index + 1) * 40}ms;
    }
  `);
/**
 * The rail is drawn per step as two segments — above and below the mark — so
 * it breaks around each mark without painting the mark in the surface colour.
 */
export const Step = styled.li `
  position: relative;
  display: grid;
  grid-template-columns: ${MARK_SIZE} 1fr;
  column-gap: ${theme.spacing.md};
  padding: var(--rail-gap) 0;
  transition:
    opacity 200ms ease,
    transform 200ms ease;
  ${staggerSteps};

  ${Panel}[data-starting-style] &,
  ${Panel}[data-ending-style] & {
    opacity: 0;
    transform: translateY(${pxToRem(-2)});
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: calc(${MARK_SIZE} / 2);
    width: ${pxToRem(1)};
    background: ${theme.swatches.warm[200]};
  }

  &::before {
    top: 0;
    height: var(--rail-gap);
  }

  &::after {
    top: calc(var(--rail-gap) + ${MARK_SIZE});
    bottom: 0;
  }

  &:first-child::before,
  &:last-child::after {
    display: none;
  }
`;
export const Mark = styled.span `
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${MARK_SIZE};
  height: ${MARK_SIZE};
  --falcon-size: ${pxToRem(11)};

  & [data-icon='falcon'] {
    color: ${theme.swatches.warm[500]};
  }
`;
export const StepBody = styled.span `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  min-width: 0;
  text-wrap: pretty;
`;
export const StepSource = styled(Typography) `
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  --falcon-size: ${pxToRem(11)};

  & [data-icon='falcon'] {
    color: ${theme.swatches.warm[500]};
  }
`;
